import React from 'react'
import { Logger } from 'wdc-cube'
import { bindUpdate } from 'wdc-cube-react'

import { keyframes, styled } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'

import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash'
import SearchIcon from '@mui/icons-material/Search'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'

import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import InputAdornment from '@mui/material/InputAdornment'
import ListItemIcon from '@mui/material/ListItemIcon'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid'

import { formatNumber } from 'src/utils/formatter-utils'
import {
    ActionsRow,
    Breadcrumbs,
    buttonSx,
    cardSx,
    chipSx,
    classToFComponent,
    EmptyIcon,
    FCClassContext,
    Field,
    FieldHelp,
    FieldLabel,
    IconActionButton,
    inputSx,
    LifesHubChipTone,
    LifesHubConfirmDialog,
    ModuleView,
    PageSubtitle,
    PageTitle,
    SwitchRow,
    switchSx
} from 'src/utils/views'

import GroupManagerScope, { GroupRow } from '../gm_scopes'
import { CellDesc, CellIdentity, CellTitle } from 'src/utils/views/modules/admin-group-manager'

const LOG = Logger.get('GroupManagerView')

const ListingView = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    padding: '26px 28px 42px',
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 0,
    overflowX: 'hidden',
    overflowY: 'auto',
    '@media (max-width: 720px)': {
        padding: '22px 18px 36px'
    }
})

const PageHead = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 24,
    marginBottom: 12,
    '@media (max-width: 720px)': {
        flexDirection: 'column',
        '& > div:last-of-type': { width: '100%' },
        '& .MuiButton-root': { width: '100%' }
    }
})

const FiltersCard = styled(Paper)({
    ...cardSx,
    padding: 18
})

const FilterRow = styled('div')({
    display: 'grid',
    gridTemplateColumns: 'minmax(280px, 1fr) auto auto',
    gap: 12,
    alignItems: 'end',
    '@media (max-width: 1050px)': {
        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
        '& > *:first-of-type': { gridColumn: '1 / -1' }
    },
    '@media (max-width: 720px)': {
        gridTemplateColumns: 'minmax(0, 1fr)',
        '& .MuiButton-root': { width: '100%' }
    }
})

const FilterFooter = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    gap: 16,
    marginTop: 15,
    flexWrap: 'wrap'
})

const ResultsCount = styled('div')({
    color: 'var(--green-800)',
    fontWeight: 700,
    whiteSpace: 'nowrap'
})

const TableCard = styled(Paper)({
    ...cardSx,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 520,
    overflow: 'hidden'
})

const CardBody = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    flexGrow: 1,
    padding: 18
})

const EmptyState = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: '48px 20px',
    textAlign: 'center',
    flexGrow: 1,
    '& h3': { margin: 0, fontSize: 16, color: 'var(--ink)' },
    '& p': { margin: '0 0 8px', color: 'var(--muted)', maxWidth: 420 }
})

const skeletonPulse = keyframes({
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.55 }
})

const SkeletonBlock = styled('div')({
    backgroundColor: '#eef1ef',
    borderRadius: 9,
    animation: `${skeletonPulse} 1.4s ease-in-out infinite`,
    flexGrow: 1
})

const GRID_SX = {
    flexGrow: 1,
    minHeight: 0,
    border: 'none',
    fontSize: 12,
    color: 'var(--ink)',
    '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
        height: '0.8em'
    },
    '& .MuiDataGrid-columnHeaders': {
        backgroundColor: '#fbfcfb',
        borderBottom: '1px solid var(--border)',
        borderRadius: 0
    },
    '& .MuiDataGrid-columnHeaderTitle': {
        fontSize: 11.5,
        fontWeight: 700,
        color: '#2a302c'
    },
    '& .MuiDataGrid-cell': {
        borderBottom: '1px solid var(--border)'
    },
    '& .MuiDataGrid-footerContainer': {
        borderTop: '1px solid var(--border)',
        color: 'var(--muted)'
    },
    '& .MuiDataGrid-row:hover': {
        background: 'linear-gradient(90deg, var(--green-050), #fbfffb)'
    },
    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
        outline: 'none'
    },
    '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
        outline: 'none'
    }
}

const useStyles = makeStyles()({
    grid: {
        ...GRID_SX
    },
    rowMenu: {
        '& .MuiPaper-root': {
            minWidth: 208,
            padding: 7,
            borderRadius: 10,
            border: '1px solid var(--border)',
            boxShadow: 'var(--shadow-2)',
            backgroundImage: 'none'
        },
        '& .MuiMenuItem-root': {
            gap: 9,
            padding: '9px 10px',
            borderRadius: 7,
            fontSize: 13.5,
            minHeight: 0,
            '&:hover': { backgroundColor: '#f0f4f1' }
        },
        '& .MuiListItemIcon-root': {
            minWidth: 0,
            color: 'inherit',
            '& .MuiSvgIcon-root': { fontSize: 18 }
        }
    },
    dangerItem: {
        color: 'var(--danger)'
    }
})

type GroupManagerViewProps = {
    className?: string
    scope: GroupManagerScope
}

class GroupManagerClass implements FCClassContext<GroupManagerViewProps> {
    readonly emitGroupFilterChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        this.scope.onGroupFilterChanged(event.target.value).catch(LOG.caught)
    }

    readonly emitFind = () => {
        this.scope.onFind().catch(LOG.caught)
    }

    readonly emitSearchKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            event.preventDefault()
            this.emitFind()
        }
    }

    readonly emitClearFilters = () => {
        this.scope.onClearFilters().catch(LOG.caught)
    }

    readonly emitNewClick = () => {
        this.scope.onNew().catch(LOG.caught)
    }

    readonly emitShowDeletedChange = () => {
        this.scope.onShowDeletedChange().catch(LOG.caught)
    }

    readonly emitOpenRowMenu = (row: GroupRow) => (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation()
        this.rowMenuAnchor = event.currentTarget
        this.scope.onOpenRowMenu(Number(row.id)).catch(LOG.caught)
    }

    readonly emitCloseRowMenu = () => {
        this.scope.onCloseRowMenu().catch(LOG.caught)
    }

    readonly emitMenuEdit = () => {
        const groupId = this.scope.rowMenuGroupId
        if (groupId !== undefined) {
            this.scope.onEdit(groupId).catch(LOG.caught)
        }
    }

    readonly emitMenuProfile = () => {
        const groupId = this.scope.rowMenuGroupId
        if (groupId !== undefined) {
            this.scope.onShowProfile(groupId).catch(LOG.caught)
        }
    }

    readonly emitMenuDelete = () => {
        const groupId = this.scope.rowMenuGroupId
        if (groupId !== undefined) {
            this.scope.onDeleteGroup(groupId).catch(LOG.caught)
        }
    }

    readonly emitDeleteConfirmTextChange = (value: string) => {
        this.scope.onDeleteConfirmTextChanged(value).catch(LOG.caught)
    }

    readonly emitCancelConfirmDeleteGroup = () => {
        this.scope.onCancelConfirmDeleteGroup().catch(LOG.caught)
    }

    readonly emitConfirmDeleteGroup = () => {
        this.scope.onConfirmDeleteGroup().catch(LOG.caught)
    }

    // :: Fields

    scope!: GroupManagerScope
    classes!: ReturnType<typeof useStyles>['classes']
    columns = buildColumns(this.emitOpenRowMenu)

    rowMenuAnchor?: HTMLElement

    // :: Methods

    onSyncState({ scope }: GroupManagerViewProps) {
        this.scope = scope
        this.classes = useStyles().classes
    }

    render({ className, scope }: GroupManagerViewProps) {
        this.scope = bindUpdate(React, scope)

        const group = scope.groupToDelete
        const restoring = !!group?.deleted_date

        return (
            <ModuleView className={className}>
                <ListingView>
                    {this.renderPageHead()}
                    {this.renderFilters()}
                    {this.renderTable()}
                </ListingView>
                {this.renderRowMenu()}
                <LifesHubConfirmDialog
                    open={scope.deleteModalOpen}
                    title={restoring ? 'Restaurar grupo' : 'Excluir grupo'}
                    subtitle={group?.name}
                    tone={restoring ? 'blue' : 'danger'}
                    message={
                        restoring
                            ? 'O grupo volta a aparecer na listagem e os usuários vinculados a ele recuperam o acesso ao sistema.'
                            : 'O grupo sai da listagem e todos os usuários vinculados a ele perdem o acesso ao sistema. A operação pode ser desfeita depois, restaurando o grupo.'
                    }
                    confirmWord={restoring ? 'RESTAURAR' : 'EXCLUIR'}
                    typedWord={scope.deleteConfirmText}
                    onTypedWordChange={this.emitDeleteConfirmTextChange}
                    confirmLabel={restoring ? 'Restaurar grupo' : 'Excluir grupo'}
                    confirmIcon={restoring ? <RestoreFromTrashIcon /> : <DeleteOutlineIcon />}
                    footerNote="A operação vale para o grupo e para todos os usuários vinculados a ele."
                    onCancel={this.emitCancelConfirmDeleteGroup}
                    onConfirm={this.emitConfirmDeleteGroup}
                />
            </ModuleView>
        )
    }

    renderPageHead() {
        return (
            <PageHead>
                <div>
                    <Breadcrumbs>
                        Administração / <b>Grupos</b>
                    </Breadcrumbs>
                    <PageTitle>Grupos</PageTitle>
                    <PageSubtitle>
                        Cadastre os grupos de acesso, defina o administrador de cada um e a franquia de exportação que
                        eles podem consumir.
                    </PageSubtitle>
                </div>
                <ActionsRow>
                    <Button sx={buttonSx('brand')} onClick={this.emitNewClick} startIcon={<AddIcon />}>
                        Novo grupo
                    </Button>
                </ActionsRow>
            </PageHead>
        )
    }

    renderFilters() {
        const { scope } = this

        return (
            <FiltersCard elevation={0}>
                <FilterRow>
                    <Field>
                        <FieldLabel htmlFor="gmGroupFilter">Buscar</FieldLabel>
                        <TextField
                            id="gmGroupFilter"
                            size="small"
                            value={scope.groupFilter}
                            onChange={this.emitGroupFilterChange}
                            onKeyDown={this.emitSearchKeyDown}
                            placeholder="Buscar por nome do grupo"
                            sx={inputSx}
                            fullWidth
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ fontSize: 20, color: 'var(--muted)' }} />
                                        </InputAdornment>
                                    )
                                }
                            }}
                        />
                    </Field>
                    <Button sx={buttonSx()} onClick={this.emitClearFilters} startIcon={<FilterAltIcon />}>
                        Limpar filtros
                    </Button>
                    <Button sx={buttonSx()} onClick={this.emitFind} startIcon={<SearchIcon />}>
                        Buscar
                    </Button>
                </FilterRow>
                <FilterFooter>
                    <SwitchRow>
                        <Switch
                            size="small"
                            checked={scope.showDeleted}
                            onChange={this.emitShowDeletedChange}
                            sx={switchSx}
                            slotProps={{
                                input: { 'aria-label': 'Mostrar grupos excluídos' }
                            }}
                        />
                        <div>
                            <b>Mostrar excluídos</b>
                            <FieldHelp>
                                Os grupos removidos passam a aparecer na listagem e podem ser restaurados.
                            </FieldHelp>
                        </div>
                    </SwitchRow>
                    <ResultsCount>
                        {scope.waiting
                            ? 'Buscando grupos…'
                            : pluralize(scope.groups.length, 'grupo encontrado', 'grupos encontrados')}
                    </ResultsCount>
                </FilterFooter>
            </FiltersCard>
        )
    }

    renderTable() {
        const { classes, scope } = this

        if (scope.waiting) {
            return (
                <TableCard elevation={0}>
                    <CardBody>
                        <SkeletonBlock />
                    </CardBody>
                </TableCard>
            )
        }

        if (scope.groups.length === 0) {
            return (
                <TableCard elevation={0}>
                    <EmptyState>
                        <EmptyIcon>
                            <InfoOutlinedIcon />
                        </EmptyIcon>
                        <h3>Nenhum grupo encontrado</h3>
                        <p>
                            Revise o nome buscado ou inclua os grupos excluídos na listagem. Se ainda não houver um
                            grupo para este cliente, crie o primeiro.
                        </p>
                        <Button sx={buttonSx('brand')} onClick={this.emitNewClick} startIcon={<AddIcon />}>
                            Novo grupo
                        </Button>
                    </EmptyState>
                </TableCard>
            )
        }

        return (
            <TableCard elevation={0}>
                <CardBody>
                    <DataGrid
                        className={classes.grid}
                        rows={scope.groups}
                        columns={this.columns}
                        getRowId={rowId}
                        getRowHeight={rowHeight}
                        disableRowSelectionOnClick
                        disableColumnMenu
                        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                        pageSizeOptions={[10, 25, 50, 100]}
                    />
                </CardBody>
            </TableCard>
        )
    }

    renderRowMenu() {
        const { classes, scope } = this

        const row = scope.groups.find((group) => Number(group.id) === scope.rowMenuGroupId)
        const deleted = !!row?.deleted_date

        return (
            <Menu
                className={classes.rowMenu}
                anchorEl={this.rowMenuAnchor}
                open={!!row}
                onClose={this.emitCloseRowMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MenuItem onClick={this.emitMenuEdit}>
                    <ListItemIcon>
                        <EditOutlinedIcon />
                    </ListItemIcon>
                    Editar grupo
                </MenuItem>
                <MenuItem onClick={this.emitMenuProfile}>
                    <ListItemIcon>
                        <VisibilityOutlinedIcon />
                    </ListItemIcon>
                    Ver perfil do grupo
                </MenuItem>
                <Divider sx={{ margin: '6px 0', borderColor: 'var(--border)' }} />
                <MenuItem className={deleted ? undefined : classes.dangerItem} onClick={this.emitMenuDelete}>
                    <ListItemIcon>{deleted ? <RestoreFromTrashIcon /> : <DeleteOutlineIcon />}</ListItemIcon>
                    {deleted ? 'Restaurar grupo' : 'Excluir grupo'}
                </MenuItem>
            </Menu>
        )
    }
}

export const GroupManagerView = classToFComponent(GroupManagerClass, React)
export default GroupManagerView

function rowId(row: GroupRow) {
    return row.id + ''
}

function rowHeight() {
    return 58
}

function pluralize(count: number, singular: string, plural: string): string {
    return `${formatNumber(count)} ${count === 1 ? singular : plural}`
}

function statusOf(row: GroupRow): { label: string; tone: LifesHubChipTone } {
    if (row.deleted_date) {
        return { label: 'Excluído', tone: 'red' }
    }

    return row.active ? { label: 'Ativo', tone: 'green' } : { label: 'Inativo', tone: 'gray' }
}

function buildColumns(openRowMenu: (row: GroupRow) => (event: React.MouseEvent<HTMLElement>) => void): GridColDef[] {
    return [
        {
            field: 'name',
            headerName: 'Grupo',
            flex: 1.6,
            minWidth: 260,
            sortable: true,
            renderCell({ row }: GridRenderCellParams<GroupRow>) {
                const admin = row.admin_fullname || row.admin_name

                return (
                    <CellIdentity>
                        <CellTitle title={row.name}>{row.name}</CellTitle>
                        <CellDesc title={admin || undefined}>
                            {admin ? `Administrado por ${admin}` : 'Sem administrador definido'}
                        </CellDesc>
                    </CellIdentity>
                )
            }
        },
        {
            field: 'admin_name',
            headerName: 'E-mail do administrador',
            flex: 1,
            minWidth: 220,
            renderCell({ row }: GridRenderCellParams<GroupRow>) {
                return row.admin_name ? (
                    <CellDesc title={row.admin_name}>{row.admin_name}</CellDesc>
                ) : (
                    <CellDesc>—</CellDesc>
                )
            }
        },
        {
            field: 'limit_export_quantity',
            headerName: 'Franquia de exportação',
            width: 190,
            renderCell({ row, value }: GridRenderCellParams<GroupRow, number | undefined>) {
                if (row.unlimited_data_export) {
                    return <Chip size="small" label="Ilimitada" sx={chipSx('blue')} />
                }

                return <span>{value === undefined || value === null ? '—' : formatNumber(value)}</span>
            }
        },
        {
            field: 'active',
            headerName: 'Situação',
            width: 130,
            renderCell({ row }: GridRenderCellParams<GroupRow>) {
                const status = statusOf(row)

                return <Chip size="small" label={status.label} sx={chipSx(status.tone)} />
            }
        },
        {
            field: 'actions',
            headerName: '',
            width: 64,
            align: 'right',
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell({ row }: GridRenderCellParams<GroupRow>) {
                return (
                    <IconActionButton aria-label="Mais ações" onClick={openRowMenu(row)}>
                        <MoreVertIcon sx={{ fontSize: 20 }} />
                    </IconActionButton>
                )
            }
        }
    ]
}
